import React from 'react';
import { useQuery } from '@apollo/client';
import Link from '../../../components/Link/Link';
import CurrencyConverter from '../../../components/CurrencyConverter';
import getIncomingBookingsQuery from './getIncomingBookings.graphql';

// Watch-tower feed: the latest incoming bookings (newest first), refreshed on the same cadence as the
// dashboard counters, each row linking to the full booking detail (date, assigned worker, amounts...).
// Reuses the existing admin getAllBookings query (already ordered createdAt DESC) — no new resolver.

const FONT = "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const GREY = '#5B6472';

// Status → badge color. Green-ish = moving forward, amber = waiting, red = cancelled/expired.
const STATUS_COLORS = {
  created: '#F59E0B',
  scheduled: '#2563EB',
  approved: '#0E9488',
  arrived: '#0E9488',
  reviewed: '#0E9488',
  started: '#16A34A',
  paused: '#F59E0B',
  resumed: '#16A34A',
  completed: '#374151',
  declined: '#B91C1C',
  expired: '#B91C1C',
  cancelledByUser: '#B91C1C',
  cancelledByPartner: '#B91C1C',
};

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(isNaN(v) ? v : Number(v));
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// Collapsed by default (Paul 2026-08-06): only the first rows show so the card stays
// compact on the dashboard; clicking the footer (or the header) unfolds the full feed.
const COLLAPSED_ROWS = 3;

const IncomingBookings = () => {
  const [expanded, setExpanded] = React.useState(false);
  const { data, loading } = useQuery(getIncomingBookingsQuery, {
    variables: { currentPage: 1 },
    fetchPolicy: 'network-only',
    ssr: false,
    // Same cadence as the dashboard counters — new bookings appear without a manual reload.
    pollInterval: 20000,
  });

  const rows = data?.getAllBookings?.results || [];
  const visibleRows = expanded ? rows : rows.slice(0, COLLAPSED_ROWS);
  const hiddenCount = rows.length - COLLAPSED_ROWS;

  const card = {
    background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: '18px 20px',
    fontFamily: FONT, marginBottom: 18,
  };
  const th = {
    textAlign: 'left', fontSize: 11, fontWeight: 700, color: GREY, textTransform: 'uppercase',
    letterSpacing: 0.4, padding: '8px 10px', borderBottom: '1px solid #eee', whiteSpace: 'nowrap',
  };
  const td = { fontSize: 13, color: '#1F2733', padding: '10px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' };

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <div
          onClick={() => setExpanded((v) => !v)}
          style={{ fontSize: 13, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: '#8A8A8A', cursor: 'pointer', userSelect: 'none' }}
          title={expanded ? 'Click to collapse' : 'Click to expand'}
        >
          Incoming bookings <span style={{ color: '#B0B5BD', fontWeight: 700 }}>{expanded ? '▴' : '▾'}</span>
        </div>
        <Link to="/siteadmin/jobs" style={{ fontSize: 13, fontWeight: 700, color: '#D32F2F', textDecoration: 'none' }}>
          View all →
        </Link>
      </div>

      {loading && rows.length === 0 ? (
        <div style={{ fontSize: 13, color: GREY, padding: '8px 0' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 13, color: GREY, padding: '8px 0' }}>No bookings yet — they will appear here the moment one comes in.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>#</th>
                <th style={th}>Service</th>
                <th style={th}>Client</th>
                <th style={th}>Worker</th>
                <th style={th}>Booked</th>
                <th style={th}>Job date</th>
                <th style={th}>Amount</th>
                <th style={th}>Status</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((b) => (
                <tr key={b.id}>
                  <td style={{ ...td, fontWeight: 700 }}>#{b.id}</td>
                  <td style={td}>{b.categoryDetails?.name || '—'}</td>
                  <td style={td}>{b.userDetails?.firstName || '—'}</td>
                  <td style={td}>{b.partnerDetails?.firstName || <span style={{ color: '#F59E0B', fontWeight: 600 }}>Unassigned</span>}</td>
                  <td style={td}>{fmtDate(b.createdAt)}</td>
                  <td style={td}>{fmtDate(b.scheduledAt)}</td>
                  <td style={td}>{b.totalFare != null ? <CurrencyConverter from={b.currency} amount={b.totalFare} /> : '—'}</td>
                  <td style={td}>
                    <span style={{
                      display: 'inline-block', fontSize: 11, fontWeight: 700, color: '#fff', borderRadius: 999,
                      padding: '3px 10px', background: STATUS_COLORS[b.status] || GREY,
                    }}>
                      {b.status}
                    </span>
                  </td>
                  <td style={td}>
                    <Link to={`/siteadmin/jobs/view/${b.id}`} style={{ fontSize: 13, fontWeight: 700, color: '#D32F2F', textDecoration: 'none' }}>
                      Details
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {hiddenCount > 0 && (
            <div
              onClick={() => setExpanded((v) => !v)}
              style={{
                textAlign: 'center', padding: '9px 0 2px', fontSize: 13, fontWeight: 700,
                color: '#D32F2F', cursor: 'pointer', userSelect: 'none',
              }}
            >
              {expanded ? 'Show less ▴' : `Show ${hiddenCount} more ▾`}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IncomingBookings;
